// theme.ts

import { type ThemeConfig, extendTheme, createMultiStyleConfigHelpers } from '@chakra-ui/react';
import { type StyleFunctionProps, mode } from '@chakra-ui/theme-tools';
import { inputAnatomy } from '@chakra-ui/anatomy';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(inputAnatomy.keys);

const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false,
};

const semanticTokens = {
  colors: {
    listItemBackground: { default: 'gray.100', _dark: 'whiteAlpha.200' },
    listItemBackgroundOpaque: { default: 'gray.100', _dark: 'gray.600' },
    bgBlue: '#0d0b2d',
    babyBlue: '#19C8F5',
    /** Light gray bands on marketing home; cards on game UI */
    surfaceMuted: { default: '#F6F7F9', _dark: 'gray.700' },
    surfaceCard: { default: 'white', _dark: 'gray.600' },
    surfaceInset: { default: '#e2e3e5', _dark: 'gray.600' },
    brandAccent: { default: '#27ADE4', _dark: 'babyBlue' },
    homeBorder: { default: 'blackAlpha.200', _dark: 'whiteAlpha.300' },
    homeBorderStrong: { default: '#232323', _dark: 'whiteAlpha.400' },
    modalClose: { default: 'gray.800', _dark: 'gray.100' },
  },
};

const styles = {
  global: (props: StyleFunctionProps) => ({
    body: {
      bg: mode('white', semanticTokens.colors.bgBlue)(props),
      color: mode('gray.800', 'white')(props),
    },
  }),
};

const defaultTextColor = {
  baseStyle: (props: StyleFunctionProps) => ({
    color: mode('gray.800', 'white')(props),
  }),
};

const baseInputStyle = definePartsStyle((props: StyleFunctionProps) => ({
  field: {
    color: mode('gray.800', 'white')(props),
    bg: mode('white', 'gray.700')(props),
    borderColor: mode('blackAlpha.200', 'whiteAlpha.300')(props),
  },
}));

const inputTheme = defineMultiStyleConfig({
  baseStyle: baseInputStyle,
});

const components = {
  Text: defaultTextColor,
  Heading: defaultTextColor,
  FormLabel: defaultTextColor,
  Tab: defaultTextColor,
  Td: defaultTextColor,
  Input: inputTheme,
  NumberInput: inputTheme,
  ModalCloseButton: {
    baseStyle: (props: StyleFunctionProps) => ({
      color: mode('gray.800', 'gray.100')(props),
    }),
  },
};

const fonts = {
  heading: 'Montserrat, Jost, Roboto, sans-serif',
  body: '"Open Sans", Montserrat, Jost, Roboto, sans-serif',
  mono: '"Open Sans", Menlo, monospace',
};

const fontWeights = {
  hairline: 100,
  thin: 200,
  light: 300,
  normal: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};

const colors = {
  gray: {
    800: '#0d0b2d',
    700: '#13103f',
    600: '#25234E',
  },
};

const theme = extendTheme({
  config,
  semanticTokens,
  fonts,
  fontWeights,
  styles,
  components,
  colors,
});

export default theme;
